// Short badge labels for NetSuite document types — "NS PO" / "NS Bid" instead
// of the full display name, for use anywhere space is tight (list badges,
// request page headers). Mirrors bcEntityLabels.js so the two integrations'
// badges read as one visual language rather than two conventions.
//
// Keyed by ns_entity_type, so a new document type just needs an entry here; an
// unrecognised key still renders something reasonable rather than nothing.
// Midwich's Special Pricing records reached through the generic record-type
// picker ARE the built-in 'vendor_bid' document type — same records, same
// review screen. Mirrors canonical_ns_entity_type in netsuite_service.py.
const NS_ENTITY_ALIASES = {
  customrecord_jcs_special_pricing: 'vendor_bid',
  customrecord_jcs_sp_item_line: 'vendor_bid',
}

/**
 * Normalise a NetSuite document-type key: trimmed, lower-cased, with the
 * Special Pricing record ids collapsed onto 'vendor_bid'. '' for a blank key.
 * @param {string} key
 * @returns {string}
 */
export function canonicalNsEntityType(key) {
  const normalised = String(key ?? '').trim().toLowerCase()
  return NS_ENTITY_ALIASES[normalised] || normalised
}

const NS_ENTITY_SHORT_LABELS = {
  purchase_order: 'NS PO',
  vendor_bid: 'Special Pricing',
}

const NS_ENTITY_DISPLAY_NAMES = {
  purchase_order: 'Purchase Order',
  vendor_bid: 'Samsung Special Pricing',
}

/**
 * @param {string} entityTypeKey - e.g. 'purchase_order', 'vendor_bid'
 * @returns {string} short badge text, e.g. 'NS Bid'
 */
export function nsEntityShortLabel(entityTypeKey) {
  if (entityTypeKey && NS_ENTITY_SHORT_LABELS[entityTypeKey]) {
    return NS_ENTITY_SHORT_LABELS[entityTypeKey]
  }
  if (entityTypeKey) {
    // Derive an abbreviation from the key's initials, e.g. 'vendor_bill' ->
    // 'NS VB', so a new document type gets a sensible badge before anyone
    // remembers to add it to the map above. A one-word key (admin-configured
    // record types like 'purchaserequisition') has a single initial, and
    // 'NS P' tells nobody anything, so those just say NetSuite.
    const initials = String(entityTypeKey)
      .split(/[_\s]+/)
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 4)
    return initials.length >= 2 ? `NS ${initials}` : 'NetSuite'
  }
  return 'NS'
}

// Lower-case nouns for running prose ("detects which purchase-order fields…",
// "sampled 50 vendor bids"). Kept here rather than inlined per component so a
// Vendor Bid template never reads "PO" anywhere in the setup/validation UI.
const NS_ENTITY_NOUNS = {
  purchase_order: { singular: 'purchase order', plural: 'purchase orders', hyphenated: 'purchase-order', abbrev: 'PO', abbrevPlural: 'POs' },
  vendor_bid: { singular: 'vendor bid', plural: 'vendor bids', hyphenated: 'vendor-bid', abbrev: 'Bid', abbrevPlural: 'bids' },
}

/**
 * Nouns for one NetSuite document type, for use in prose.
 * @param {string} entityTypeKey
 * @returns {{singular:string, plural:string, hyphenated:string, abbrev:string, abbrevPlural:string}}
 */
export function nsEntityNouns(entityTypeKey) {
  return NS_ENTITY_NOUNS[entityTypeKey] || NS_ENTITY_NOUNS.purchase_order
}

/**
 * @param {string} entityTypeKey
 * @returns {string} full display name, e.g. 'Vendor Bid'
 */
export function nsEntityDisplayName(entityTypeKey) {
  if (entityTypeKey && NS_ENTITY_DISPLAY_NAMES[entityTypeKey]) {
    return NS_ENTITY_DISPLAY_NAMES[entityTypeKey]
  }
  if (!entityTypeKey) return 'NetSuite Document'
  return String(entityTypeKey)
    .split(/[_\s]+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}
